/**
 * File: taxYear.ts
 * Path: packages/config/src/accounting/taxYear.ts
 * Description: The things about a tax year that the books can't know: costs
 * that aren't deductible, equipment allowances, an assessed loss from an
 * earlier year, tax already paid. Kept per financial year at
 * accounting/{profileId}/taxYears/{yearEnd} (yearEnd is the last day of the
 * year, e.g. "2027-02-28"), written only by the `accounting` callable.
 */

import { isBooksDate } from "./ledger";

export interface TaxYearInputs {
  /** The financial year's last day; also the document id. */
  yearEnd: string;
  /** Costs the books show that tax doesn't allow (entertainment, fines, penalties). */
  nonDeductibleCents: number;
  /** Other amounts to add to the profit for tax. */
  otherAdditionsCents: number;
  /** Wear-and-tear allowance on top of what the equipment list works out. */
  wearAndTearCents: number;
  /** Other amounts tax lets you take off. */
  otherDeductionsCents: number;
  /** Doubtful debt allowance, s11(j): tax's version of a provision for debts that may not be paid. */
  doubtfulDebtCents: number;
  /** An assessed loss carried forward from earlier years. */
  assessedLossCents: number;
  /** Provisional tax paid for this year. */
  provisional: { firstCents: number; secondCents: number; topUpCents: number };
  /** The journal each provisional payment was posted as ("" when it wasn't posted from here). */
  provisionalJournals: { first: string; second: string; topUp: string };
  /** The taxable income each provisional payment was worked out on (nil: not recorded). */
  estimates: { firstCents: number; secondCents: number };
  /** Last year's assessed taxable income less capital gains: SARS's floor for the estimate. */
  basicAmountCents: number;
  /** Net capital gain this year (before the company's 80% inclusion). */
  capitalGainCents: number;
  /** Dividends the company declared this year. */
  dividendsDeclaredCents: number;
  /** Tax already deducted from pay (PAYE on a salary or IRP5). */
  payeCents: number;
  /** Pays staff, so monthly PAYE, UIF and SDL (EMP201) and the staff reconciliations apply. */
  hasEmployees: boolean;
  /** Registered for VAT, so VAT returns apply. */
  vatRegistered: boolean;
  /** VAT filing group: A (odd months), B (even months), C (monthly). */
  vatCategory: "A" | "B" | "C";
  /** Main activity SIC code (four or five digits), or "" until chosen. */
  sicCode: string;
  /** Full-time employees who aren't owners or their family: three or more lifts the personal-service rule. */
  qualifyingEmployees: number;
  /** The share (0 to 100) of sales that is the owners' own personal work; null until answered. */
  personalServicePercent: number | null;
  notes: string;
  updatedAt: number | null;
  updatedBy: string;
}

export const TAX_YEAR_COLLECTION = "taxYears";

export const emptyTaxYear = (yearEnd: string): TaxYearInputs => ({
  yearEnd,
  nonDeductibleCents: 0,
  otherAdditionsCents: 0,
  wearAndTearCents: 0,
  otherDeductionsCents: 0,
  doubtfulDebtCents: 0,
  assessedLossCents: 0,
  provisional: { firstCents: 0, secondCents: 0, topUpCents: 0 },
  provisionalJournals: { first: "", second: "", topUp: "" },
  estimates: { firstCents: 0, secondCents: 0 },
  basicAmountCents: 0,
  capitalGainCents: 0,
  dividendsDeclaredCents: 0,
  payeCents: 0,
  hasEmployees: false,
  vatRegistered: false,
  vatCategory: "B",
  sicCode: "",
  qualifyingEmployees: 0,
  personalServicePercent: null,
  notes: "",
  updatedAt: null,
  updatedBy: "",
});

type Raw = Record<string, unknown>;
const cents = (v: unknown): number => (typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : 0);
const count = (v: unknown): number => (typeof v === "number" && Number.isSafeInteger(v) && v >= 0 && v <= 10_000 ? v : 0);
const percent = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100 ? v : null);

/** Standard Industrial Classification codes are four or five digits. */
export const isSicCode = (v: string): boolean => /^\d{4,5}$/.test(v);

export const toTaxYear = (id: string, data: Raw | undefined | null): TaxYearInputs => {
  const r = data ?? {};
  const p = (r.provisional ?? {}) as Raw;
  const e = (r.estimates ?? {}) as Raw;
  const j = (r.provisionalJournals ?? {}) as Raw;
  const jid = (v: unknown) => (typeof v === "string" && v.length <= 128 ? v : "");
  const base = emptyTaxYear(isBooksDate(id) ? id : "");
  const updatedAt = r.updatedAt as { toMillis?: () => number } | number | null | undefined;
  return {
    ...base,
    nonDeductibleCents: cents(r.nonDeductibleCents),
    otherAdditionsCents: cents(r.otherAdditionsCents),
    wearAndTearCents: cents(r.wearAndTearCents),
    otherDeductionsCents: cents(r.otherDeductionsCents),
    doubtfulDebtCents: cents(r.doubtfulDebtCents),
    assessedLossCents: cents(r.assessedLossCents),
    provisional: { firstCents: cents(p.firstCents), secondCents: cents(p.secondCents), topUpCents: cents(p.topUpCents) },
    provisionalJournals: { first: jid(j.first), second: jid(j.second), topUp: jid(j.topUp) },
    estimates: { firstCents: cents(e.firstCents), secondCents: cents(e.secondCents) },
    basicAmountCents: cents(r.basicAmountCents),
    capitalGainCents: cents(r.capitalGainCents),
    dividendsDeclaredCents: cents(r.dividendsDeclaredCents),
    payeCents: cents(r.payeCents),
    hasEmployees: r.hasEmployees === true,
    vatRegistered: r.vatRegistered === true,
    vatCategory: r.vatCategory === "A" || r.vatCategory === "C" ? r.vatCategory : "B",
    sicCode: typeof r.sicCode === "string" && isSicCode(r.sicCode) ? r.sicCode : "",
    qualifyingEmployees: count(r.qualifyingEmployees),
    personalServicePercent: percent(r.personalServicePercent),
    notes: typeof r.notes === "string" ? r.notes : "",
    updatedAt:
      updatedAt && typeof updatedAt === "object" && typeof updatedAt.toMillis === "function"
        ? updatedAt.toMillis()
        : typeof updatedAt === "number"
          ? updatedAt
          : null,
    updatedBy: typeof r.updatedBy === "string" ? r.updatedBy : "",
  };
};

export const provisionalPaidCents = (t: TaxYearInputs): number =>
  t.provisional.firstCents + t.provisional.secondCents + t.provisional.topUpCents;

export type ProvisionalPeriod = "first" | "second" | "topUp";

export const PROVISIONAL_PERIOD_NUMBER: Record<ProvisionalPeriod, 1 | 2 | 3> = { first: 1, second: 2, topUp: 3 };

/** The calendar id of a provisional payment (taxCalendar.ts) for a financial year. */
export const provisionalObligationId = (period: ProvisionalPeriod, yearEnd: string): string =>
  `irp6-${PROVISIONAL_PERIOD_NUMBER[period]}-${yearEnd}`;

/** The account provisional payments are posted to: tax paid ahead of the assessment. */
export const PROVISIONAL_TAX_ACCOUNT = {
  code: "1250",
  name: "Provisional tax paid",
  type: "asset",
  taxLine: "Other current assets",
} as const;
